import React, { cloneElement, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import ConfirmCloseModal from "../Modals/ConfirmCloseModal";
import { trapFocus, focusFirstElement } from "../../helpers/focusHelper";
import PropTypes from "prop-types";
import { isEsc } from "../../helpers/keyHelper";

const modalRoot = document.getElementById("modal-container");

function createModal(props) {
  /**
   *
   * Handles the click event for the "X" (Close) button on a modal, it checks if the modal being shown (e.g
   * AddContributionModal) should show a confirm close message when trying to close it
   If the "confirmClose" prop is true then we set showConfirmCloseModal to true which displays the ConfirmCloseModal, and sets "confirmCloseModelisOpen" to true so that we know the confirm close modal is displayed on screen
   If the "confirmClose" prop is false, we do not need to show a confirm close message, so both are set to false
   * @param {boolean} confirmClose Boolean to state whether a modal should have a confirm close message modal
   * @param {Function} closeRootModal This is the function to close the parent modal of the close message modal
   * @param {Function} dismissError This is the function to dismiss all errors when the modal is closed
   * @param {Event} [e=null] The event which occurs on click, used to stop event bubbling
   */
  const handleCloseButtonClick = (
    confirmClose,
    closeRootModal,
    dismissError,
    e = null
  ) => {
    if (dismissError !== undefined) {
      dismissError();
    }

    if (e) {
      //Stops the event bubbling
      e.preventDefault();
    }
    if (confirmClose === true) {
      props.setShowConfirmCloseModal(true);
      props.setConfirmCloseModalIsOpen(true);
    } else {
      props.setShowConfirmCloseModal(false);
      props.setConfirmCloseModalIsOpen(false);
      closeRootModal();
    }
  };

  return (
    <div ref={props.node}>
      {props.showConfirmCloseModal === true && (
        <Modal
          closeRootModal={props.closeRootModal}
          confirmClose={false}
          confirmCloseModalIsOpen={true}
          showConfirmCloseModal={false}
          node={props.node}
          isSmallModal={true}
        >
          <ConfirmCloseModal
            closeRootModal={props.closeRootModal}
            setShowConfirmCloseModal={props.setShowConfirmCloseModal}
            setConfirmCloseModalIsOpen={props.setConfirmCloseModalIsOpen}
            setResetFocus={props.setResetFocus}
          />
        </Modal>
      )}
      <div
        className={`modal modal--ugc ${props.confirmCloseModalIsOpen ? 'modal--ugc-confirm-close' : ''} ${props.isSmallModal ? 'modal--ugc-small-modal' : ''}`}>
        <div className="container container--padding">
          <div className="he-grid-row modal--ugc-container-spacing">
            <div className="modal__col he-grid-col-md-10 mx-auto">
              <div className="modal__content-wrapper modal__content-wrapper-close">

                {!props.confirmCloseModalIsOpen && (
                  <button
                    id="closeModal"
                    onClick={(e) =>
                      handleCloseButtonClick(
                        props.confirmClose,
                        props.closeRootModal,
                        props.dismissError,
                        e
                      )
                    }
                    type="button"
                    className="modal__close-button"
                    aria-label="Close"
                    data-moreinformation={props.moreInformation}
                  >
                    <span className="sr-only" aria-hidden="true">
                      Close
                    </span>
                  </button>
                )}

                <div className="modal__content-wrapper-container">
                  <div className="container modal__content-wrapper-container-child">
                    {cloneElement(props.children, { handleCloseButtonClick })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

Modal.propTypes = {
  maxChars: PropTypes.number,
  closeRootModal: PropTypes.func,
  confirmClose: PropTypes.bool,
  showConfirmCloseModal: PropTypes.bool,
  setShowConfirmCloseModal: PropTypes.func,
  confirmCloseModalIsOpen: PropTypes.bool,
  setConfirmCloseModalIsOpen: PropTypes.func,
  isSmallModal: PropTypes.bool,
  dismissError: PropTypes.func
};

function Modal(props) {
  /** @type {boolean} Used to show and hide the "ConfirmCloseModal" when trying to close the parent modal */
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(
    !!props.showConfirmCloseModal
  );

  const [resetFocus, setResetFocus] = useState(!!props.resetFocus);

  /** @type {boolean} Used to determine whether or not the "ConfirmCloseModal is currently displayed on screen or not*/
  const [confirmCloseModalIsOpen, setConfirmCloseModalIsOpen] = useState(
    !!props.confirmCloseModalIsOpen
  );

  const node = useRef(null);

  useEffect(() => {
    trapFocus(node.current);
    focusFirstElement(node.current);
    setResetFocus(false);

    const handleEsc = (e) => {
      if (isEsc(e)) {
        // If the id of the target is "closeModal" then trigger the click
        // If it isn't (i.e. the user has focused on another element in the modal) find the button and then trigger the
        // click
        if (e.target.id === "closeModal") {
          e.target.click();
        } else {
          // Check if the target is the link for the help text, and find the button to close that modal (the button
          // will have a data attribute of moreinformation) We do this so it only closes the one modal and doesn't
          // trigger the 'confirm close' modal
          const closeModalButton = e.target.id === "altTextHelpLink" ? document.querySelector("[data-moreinformation='true']") : document.getElementById("closeModal");
          closeModalButton?.click();
        }
      }
    };
    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [props.confirmCloseModalIsOpen, props.showConfirmCloseModal, resetFocus]);

  return createPortal(
    createModal({
      ...props,
      showConfirmCloseModal,
      setShowConfirmCloseModal,
      confirmCloseModalIsOpen,
      setConfirmCloseModalIsOpen,
      node,
      setResetFocus,
    }),
    modalRoot
  );
}

export default Modal;